import { cloneDeep, isEmpty } from '@block/utils';
import { EduType } from '@src/nodes/educationInfo/types';

/**
 * 获取教育经历类型
 * @param data 教育经历数据
 * @returns 教育经历类型
 */
export const getEduType = (data, isIntern) => {
  const { countryDictIndex = 'CHN', diplomaDictIndex = '', isTopDegree = false } = data || {};
  // 非最高学历，不需要展示上传内容
  if (!isTopDegree) {
    return EduType.NotTopDegree;
  }

  // 实习生
  if (isIntern) {
    // 国内实习生
    if (countryDictIndex === 'CHN') {
      return EduType.InternChinese;
    }

    // 海外实习生
    return EduType.InternForeign;
  }

  // ----- 非实习生 --------

  // 国内 非实习生
  if (countryDictIndex === 'CHN') {
    // 64:高中/中专及以下 67:专科
    if (['64', '67'].includes(diplomaDictIndex)) {
      /** 非实习生-中国-专科 */
      return EduType.ChineseCollege;
    }

    /** 非实习生-中国-非专科 */
    return EduType.ChineseNonCollege;
  }

  // 海外 非实习生

  // 64:高中/中专及以下 67:专科
  if (['64', '67'].includes(diplomaDictIndex)) {
    /** 非实习生-非中国-专科 */
    return EduType.ForeignCollege;
  }

  /** 非实习生-中国-非专科 */
  return EduType.ForeignNonCollege;
};

/** isTopDegree 适配为 布尔值 */
const TURN_IS_TOP_DEGREE_TO_BOOLEAN = 'boolean';

/**
 * 处理最高学历的适配
 * @param data 学历数据
 * @param type 转换类型
 * - 当type为TURN_IS_TOP_DEGREE_TO_BOOLEAN时：将字符串'Y'/'N'转为布尔值true/false，用于前端展示
 * - 当type为undefined时：将布尔值true/false转为字符串'Y'/'N'，用于后端存储
 * @returns 处理后的学历数据
 */
export const handleAdapterTopDegree = (data, type?: typeof TURN_IS_TOP_DEGREE_TO_BOOLEAN) => {
  if (!data) return data;

  return data.map((item) => {
    const result = { ...item };
    const { isTopDegree } = result;

    // 转换为布尔值：用于前端展示
    if (type === TURN_IS_TOP_DEGREE_TO_BOOLEAN) {
      // 如果已经是布尔值，保持不变；否则将'Y'转为true，其他值转为false
      result.isTopDegree = typeof isTopDegree === 'boolean' ? isTopDegree : isTopDegree === 'Y';
    } else if (isTopDegree !== 'Y' && isTopDegree !== 'N') {
      // 转换为字符串：用于后端存储
      // 如果不是'Y'或'N'，则将布尔值或其他值转换为'Y'或'N'
      result.isTopDegree = isTopDegree ? 'Y' : 'N';
    }
    return result;
  });
};

/**
 * 处理在途数据和最高学历布尔值转换
 */
export const handleAdapterData = (data) => {
  const cloneData = cloneDeep(data);
  const { oldFileList = [], degree, employeeType, ...rest } = cloneData;
  // 判断员工类型 employeeType 可能是 intern 或 regular 或 其他，只需要判断是否是实习生
  // 线上后端将regular写成reguler，是错别字，要注意
  const isIntern = employeeType === 'intern';

  // 判断是否有在途数据
  const hasOldData = !isEmpty(oldFileList);

  // 有在途数据的话，需要把oldFileList中的附件，放入到最高全日制教育经历的附件字段中，实习生的不放进去，全部清空
  if (!isIntern && hasOldData) {
    const topFullTimeDegree = degree?.find((item) => {
      const { educationTypeDictIndex, isTopDegree } = item;
      // 全日制教育经历，并且是最高学历
      return educationTypeDictIndex === '71' && isTopDegree;
    });

    if (topFullTimeDegree) {
      topFullTimeDegree.attachmentList = oldFileList;

      // 删除 educationCode 和 degreeCode 以防脏数据
      topFullTimeDegree.educationCode = null;
      topFullTimeDegree.degreeCode = null;
    }
  }

  // 没有在途数据的话，只需要处理其他字段即可，在最后删除掉在途数据字段

  // 处理最高学历的适配
  const degreeAdapter = handleAdapterTopDegree(degree, TURN_IS_TOP_DEGREE_TO_BOOLEAN);

  // 删掉 oldFileList
  const result = {
    degree: degreeAdapter,
    isIntern,
    employeeType,
    ...rest,
  };

  return result;
};
